import {
  type AddressSearchResult,
  getAddressDetail,
  searchAddresses,
} from '@/domains/jobs/api/jobDetailApi';
import { useUpdateJobDetail } from '@/domains/jobs/hooks/useUpdateJobDetail';
import { showSuccessToast } from '@/shared/notifications/toast-utils';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import {
  AlertTriangle,
  Building2,
  Calendar,
  Clock,
  Edit2,
  FileText,
  Layers,
  Loader2,
  MapPin,
  Save,
  Search,
  Tag,
  User,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { InfoRow } from './InfoRow';

interface JobDetailsData {
  id?: string | number;
  client: string;
  address: string;
  assignee?: string;
  dueDate?: string;
  requiredBy?: string;
  estimatedHours?: number;
  duration?: number;
  createdDate?: string;
  description?: string;
  tags?: string[];
  category?: string;
}

export function JobDetailsCard({ jobData }: { jobData: JobDetailsData }) {
  const [isEditingDuration, setIsEditingDuration] = useState(false);
  const [editedDuration, setEditedDuration] = useState(jobData.duration || 4);
  const [dcrConfirmOpen, setDcrConfirmOpen] = useState(false);
  const [dcrResultOpen, setDcrResultOpen] = useState(false);
  const currentDuration = jobData.duration || 4;
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [addressQuery, setAddressQuery] = useState('');
  const [addressResults, setAddressResults] = useState<AddressSearchResult[]>([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const addressDebounce = useRef<ReturnType<typeof setTimeout>>();

  // Keep the editor in sync with fresh server data when the job loads / refetches.
  useEffect(() => {
    if (!isEditingDuration) {
      setEditedDuration(jobData.duration || 4);
    }
  }, [jobData.duration, isEditingDuration]);

  const numericJobId = Number(jobData.id);
  const canSave = Number.isFinite(numericJobId) && numericJobId > 0;
  const updateMutation = useUpdateJobDetail(canSave ? numericJobId : 0);

  const wouldTriggerDcr = currentDuration > 0 && editedDuration > currentDuration * 1.5;

  const handleSaveDuration = async () => {
    if (!canSave) return;
    if (wouldTriggerDcr) {
      setDcrConfirmOpen(true);
      return;
    }
    await doSaveDuration();
  };

  const doSaveDuration = async () => {
    if (!canSave) return;
    try {
      const result = await updateMutation.mutateAsync({ duration: editedDuration });
      setIsEditingDuration(false);
      setDcrConfirmOpen(false);
      if ((result as any)?._durationChallenge) {
        setDcrResultOpen(true);
      } else {
        showSuccessToast('Duration updated');
      }
    } catch {
      // Global mutation error handler in query-client shows a toast.
    }
  };

  const handleCancelEdit = () => {
    setEditedDuration(jobData.duration || 4);
    setIsEditingDuration(false);
  };

  const handleAddressSearch = useCallback((query: string) => {
    setAddressQuery(query);
    clearTimeout(addressDebounce.current);
    if (query.length < 2) {
      setAddressResults([]);
      return;
    }
    addressDebounce.current = setTimeout(async () => {
      setAddressLoading(true);
      try {
        const results = await searchAddresses(query);
        setAddressResults(results);
      } catch {
        setAddressResults([]);
      }
      setAddressLoading(false);
    }, 300);
  }, []);

  const handleAddressSelect = useCallback(
    async (result: AddressSearchResult) => {
      if (result.type === 'Address') {
        setAddressLoading(true);
        try {
          const detail = await getAddressDetail(result.id);
          const fullAddress = [detail.line1, detail.line2, detail.city, detail.postcode]
            .filter(Boolean)
            .join(', ');
          const coords =
            detail.latitude && detail.longitude
              ? `POINT(${detail.longitude} ${detail.latitude})`
              : null;
          await updateMutation.mutateAsync({
            address: fullAddress,
            town: detail.city,
            county: detail.county,
            pincode: detail.postcode,
            coordinates: coords,
          });
          showSuccessToast('Address updated');
          setIsEditingAddress(false);
          setAddressQuery('');
          setAddressResults([]);
        } catch {
          // error toast handled by query client
        }
        setAddressLoading(false);
      } else {
        // Container type — drill down
        setAddressLoading(true);
        try {
          const results = await searchAddresses(result.text, result.id);
          setAddressResults(results);
          setAddressQuery(result.text);
        } catch {
          setAddressResults([]);
        }
        setAddressLoading(false);
      }
    },
    [updateMutation],
  );

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-orange-50 dark:bg-orange-950 flex items-center justify-center">
          <FileText className="w-5 h-5 text-orange-600 dark:text-orange-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Job Details</h3>
          <p className="text-xs text-muted-foreground">Core job information</p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 text-sm">
          <InfoRow icon={Building2} label="Client" value={jobData.client} />

          <InfoRow icon={Layers} label="Job Category" value={jobData.category ?? 'Not set'} />

          {/* Site Address — editable with Loqate */}
          <div className="flex items-start gap-3">
            <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
            <div className="flex-1">
              <div className="text-xs text-muted-foreground mb-1">Site Address</div>
              {isEditingAddress ? (
                <div className="space-y-2">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                    <Input
                      value={addressQuery}
                      onChange={(e) => handleAddressSearch(e.target.value)}
                      placeholder="Start typing an address..."
                      className="pl-8 h-8 text-sm"
                      autoFocus
                    />
                    {addressLoading && (
                      <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground animate-spin" />
                    )}
                  </div>
                  {addressResults.length > 0 && (
                    <div className="border rounded-lg max-h-[200px] overflow-y-auto">
                      {addressResults.map((result) => (
                        <button
                          key={result.id}
                          type="button"
                          onClick={() => handleAddressSelect(result)}
                          className="w-full flex items-start gap-2 px-3 py-2 hover:bg-muted/50 text-left text-sm border-b last:border-b-0"
                        >
                          <MapPin className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-foreground">{result.text}</div>
                            {result.description && (
                              <div className="text-xs text-muted-foreground">
                                {result.description}
                              </div>
                            )}
                          </div>
                          {result.type !== 'Address' && (
                            <span className="text-xs text-muted-foreground flex-shrink-0">▸</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs"
                    onClick={() => {
                      setIsEditingAddress(false);
                      setAddressQuery('');
                      setAddressResults([]);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-foreground">{jobData.address || 'Not set'}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                    onClick={() => setIsEditingAddress(true)}
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
            </div>
          </div>
          {jobData.assignee && <InfoRow icon={User} label="Assigned To" value={jobData.assignee} />}
          {(jobData.dueDate || jobData.requiredBy) && (
            <InfoRow
              icon={Calendar}
              label={jobData.requiredBy ? 'Required By' : 'Due Date'}
              value={
                jobData.requiredBy
                  ? new Date(jobData.requiredBy).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })
                  : new Date(jobData.dueDate!).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })
              }
            />
          )}
          {jobData.estimatedHours && (
            <InfoRow icon={Clock} label="Estimated Hours" value={`${jobData.estimatedHours}h`} />
          )}

          {/* Duration with inline editing */}
          <div className="flex items-start gap-3">
            <Clock className="w-4 h-4 text-muted-foreground mt-0.5" />
            <div className="flex-1">
              <div className="text-xs text-muted-foreground mb-1">Duration</div>
              {isEditingDuration ? (
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={editedDuration}
                    onChange={(e) => setEditedDuration(Number(e.target.value))}
                    className="h-8 w-24"
                    min={1}
                  />
                  <span className="text-sm text-muted-foreground">hours</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0 text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-950"
                    onClick={handleSaveDuration}
                    disabled={updateMutation.isPending || !canSave}
                  >
                    <Save className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                    onClick={handleCancelEdit}
                    disabled={updateMutation.isPending}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-foreground font-medium">
                    {jobData.duration || editedDuration}{' '}
                    {(jobData.duration ?? 0) >= 24
                      ? `hours (${Math.floor((jobData.duration || editedDuration) / 24)} days)`
                      : 'hours'}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                    onClick={() => setIsEditingDuration(true)}
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
            </div>
          </div>

          {jobData.createdDate && (
            <InfoRow
              icon={Calendar}
              label="Created Date"
              value={new Date(jobData.createdDate).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })}
            />
          )}
        </div>

        {jobData.description && (
          <div className="pt-4 border-t border-border">
            <div className="text-xs text-muted-foreground mb-2">Description</div>
            <p className="text-sm text-foreground">{jobData.description}</p>
          </div>
        )}

        {jobData.tags && jobData.tags.length > 0 && (
          <div className="pt-4 border-t border-border">
            <div className="text-xs text-muted-foreground mb-2">Tags</div>
            <div className="flex items-center gap-2 flex-wrap">
              {jobData.tags.map((tag: string, idx: number) => (
                <Badge key={idx} variant="outline" className="text-xs">
                  <Tag className="w-3 h-3 mr-1" />
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Duration Challenge Confirmation */}
      <Dialog open={dcrConfirmOpen} onOpenChange={setDcrConfirmOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-50 dark:bg-amber-950 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <DialogTitle>Duration Challenge Required</DialogTitle>
                <DialogDescription className="mt-1">
                  This duration change exceeds the current estimate by more than 50% and will
                  require approval from Ignite Core before the job can proceed.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="rounded-md bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 p-3 text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-amber-700 dark:text-amber-300">Current Duration</span>
              <span className="font-medium text-amber-900 dark:text-amber-100">
                {currentDuration} hours
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-amber-700 dark:text-amber-300">New Duration</span>
              <span className="font-medium text-amber-900 dark:text-amber-100">
                {editedDuration} hours
              </span>
            </div>
            <div className="flex justify-between pt-1 border-t border-amber-200 dark:border-amber-700">
              <span className="text-amber-700 dark:text-amber-300">Increase</span>
              <span className="font-medium text-amber-900 dark:text-amber-100">
                {currentDuration > 0
                  ? Math.round(((editedDuration - currentDuration) / currentDuration) * 100)
                  : 0}
                %
              </span>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            The job will be placed on hold until the challenge is approved or rejected. Are you sure
            you want to proceed?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDcrConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={doSaveDuration}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Sending...' : 'Send Duration Challenge'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duration Challenge Sent Confirmation */}
      <Dialog open={dcrResultOpen} onOpenChange={setDcrResultOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Duration Challenge Sent</DialogTitle>
            <DialogDescription>
              The Duration Challenge Request has been sent to Ignite Core. The job is now on hold
              pending approval.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setDcrResultOpen(false)}>Understood</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
